import './styles.scss';
import { faArrowRotateRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import { useContext, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';

import { GalleryContext } from '../../providers/gallery-provider';
import { useGalleryWidgets } from './use-gallery-widgets';
import TokenDataWidget from '../v2/widgets/token-data';

const ResponsiveGridLayout = WidthProvider(Responsive);

export default function Gallery() {
  const { currency, setCurrency, layouts, setLayouts, resetLayout, rowExpanded } =
    useContext(GalleryContext);
  const { renderWidget } = useGalleryWidgets();

  const handleResetLayout = () => {
    resetLayout();
  };

  const renderBody = useMemo(() => {
    const widgetClassName = (token: string) => {
      if (!rowExpanded || rowExpanded.size === 0) return '';

      return rowExpanded.has(token) ? 'row-maximized' : '';
    };

    const handleLayoutChange = (
      layout: ReactGridLayout.Layout[],
      layouts: ReactGridLayout.Layouts,
    ) => {
      setLayouts(layouts);
    };

    return (
      <div className="main-content">
        <ResponsiveGridLayout
          className="grid"
          layouts={layouts}
          autoSize={true}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 }}
          resizeHandles={['se']}
          draggableHandle=".drag-area"
          onLayoutChange={handleLayoutChange}
          useCSSTransforms={false}
        >
          <div key="vol-smile" className={widgetClassName('vol-smile')}>
            {renderWidget({ gridKey: 'vol-smile' })}
          </div>
          <div key="vol-smile-compare" className={widgetClassName('vol-smile-compare')}>
            {renderWidget({ gridKey: 'vol-smile-compare' })}
          </div>
          <div
            key="model-volatility-surface"
            className={widgetClassName('model-volatility-surface')}
          >
            {renderWidget({ gridKey: 'model-volatility-surface' })}
          </div>
          <div key="term-structure" className={widgetClassName('term-structure')}>
            {renderWidget({ gridKey: 'term-structure' })}
          </div>
          <div key="term-structure-compare" className={widgetClassName('term-structure-compare')}>
            {renderWidget({ gridKey: 'term-structure-compare' })}
          </div>
          <div key="scatter-plot" className={widgetClassName('scatter-plot')}>
            {renderWidget({ gridKey: 'scatter-plot' })}
          </div>
          <div key="volatility-cones" className={widgetClassName('volatility-cones')}>
            {renderWidget({ gridKey: 'volatility-cones' })}
          </div>
          <div key="volatility-heatmap" className={widgetClassName('volatility-heatmap')}>
            {renderWidget({ gridKey: 'volatility-heatmap' })}
          </div>
          <div key="footprint-volatility" className={widgetClassName('footprint-volatility')}>
            {renderWidget({ gridKey: 'footprint-volatility' })}
          </div>
          <div key="open-interest" className={widgetClassName('open-interest')}>
            {renderWidget({ gridKey: 'open-interest' })}
          </div>
        </ResponsiveGridLayout>
      </div>
    );
  }, [currency, layouts, rowExpanded, setLayouts]);

  return (
    <div className="dashboard-container">
      <div className="token-selector-container">
        <TokenDataWidget currency={currency} setCurrency={setCurrency} />
      </div>
      {renderBody}
      <div className="layout-buttons">
        <Button onClick={handleResetLayout} className="btn-restore">
          <FontAwesomeIcon icon={faArrowRotateRight} />
          <span>Restore layout</span>
        </Button>
      </div>
    </div>
  );
}
